import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Truck, Scale, RefreshCcw, ArrowRight, MapPin, Mail, Phone } from 'lucide-react';
import { trustIndicators } from '../data/mockData';
import { useSEO } from '../hooks/useSEO';
import ContactForm from '../components/ContactForm';

const iconMap: Record<string, React.ReactNode> = {
    Truck: <Truck size={24} />,
    Scale: <Scale size={24} />,
    RefreshCcw: <RefreshCcw size={24} />,
};

const Home = () => {
    const { t } = useTranslation();

    useSEO({
        title: 'Laz Metals | Compra, Reciclaje y Venta de Metales en América',
        description: 'Empresa líder en América en logística, compra y suministro de metales ferrosos y no ferrosos. Operamos toneladas de chatarra industrial para el sector siderúrgico.',
    });

    return (
        <div className="w-full">
            {/* HERO SECTION */}
            <section className="min-h-[100dvh] massive-container bg-bg-secondary flex flex-col justify-center pt-20 lg:pt-0 pb-12 lg:pb-0 relative overflow-hidden mt-6 shadow-subtle group">
                <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-[20s] group-hover:scale-105"
                    style={{ backgroundImage: 'url(/images/hero/hero-main.png)' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-bg-tertiary/95 via-bg-tertiary/60 to-transparent" />
                <div className="absolute inset-0 bg-black/30 bg-opacity-40" />

                <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="section-tag-orange inline-flex items-center gap-2 mb-6 text-sm text-primary-light border border-primary-light/30">
                            <span className="w-2 h-2 rounded-full bg-orange-400 animate-pulse" />
                            {t('hero.eyebrow')}
                        </div>
                        <h1 className="text-white mb-6 leading-tight max-w-5xl font-heading text-3xl md:text-5xl lg:text-7xl font-bold tracking-tight drop-shadow-md">
                            {t('hero.title')}
                        </h1>
                        <p className="text-gray-300 text-lg md:text-xl font-body max-w-3xl mb-10 leading-relaxed font-light">
                            {t('hero.subtitle')}
                        </p>
                        <div className="flex flex-col sm:flex-row items-center gap-4">
                            <a href="https://api.whatsapp.com/send?phone=19292902022&text=Hola,%20vengo%20de%20la%20p%C3%A1gina%20principal.%20Me%20interesa%20cotizar%20una%20compra/venta%20de%20metales." target="_blank" rel="noopener noreferrer" className="btn-primary w-full sm:w-auto h-14 px-8 text-base shadow-lg shadow-primary/20 hover:shadow-primary/40">
                                {t('hero.ctaPrimary')}
                            </a>
                            <a href="https://api.whatsapp.com/send?phone=19292902022&text=Hola,%20me%20gustar%C3%ADa%20hablar%20con%20un%20asesor%20comercial%20de%20Laz%20Metals." target="_blank" rel="noopener noreferrer" className="btn-outline-white w-full sm:w-auto h-14 px-8 text-base hover:bg-white/10 border-white/30 backdrop-blur-sm">
                                {t('hero.ctaSecondary')}
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* TRUST BAR SECTION */}
            <section className="bg-white border-b border-gray-100 py-6 transform -translate-y-6 relative z-20 mx-auto w-[95%] max-w-6xl rounded-card shadow-[0_8px_30px_rgb(0,0,0,0.06)] px-8">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-12 divide-y md:divide-y-0 md:divide-x divide-gray-100">
                    {trustIndicators.map((indicator, idx) => (
                        <div key={idx} className={`flex items-center gap-4 ${idx !== 0 ? 'md:pl-12 pt-6 md:pt-0' : ''}`}>
                            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                                {iconMap[indicator.icon]}
                            </div>
                            <p className="font-body text-sm font-medium text-gray-700 leading-snug">
                                {t(indicator.text)}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* ABOUT US SUMMARY SECTION */}
            <section className="py-24 bg-white relative overflow-hidden">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="flex flex-col md:flex-row items-center gap-16">
                        <motion.div
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="md:w-1/2"
                        >
                            <div className="section-tag-green mb-6">{t('about.tag')}</div>
                            <h2 className="text-2xl md:text-5xl font-heading font-medium text-accent mb-6">
                                {t('about.title')}
                            </h2>
                            <p className="text-gray-600 font-body text-lg leading-relaxed mb-8">
                                {t('about.manifesto')}
                            </p>
                            <a href="/nosotros" className="inline-flex items-center text-primary font-bold text-sm uppercase tracking-wide hover:gap-3 transition-all">
                                {t('about.cta')} <ArrowRight size={16} className="ml-2" />
                            </a>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6, delay: 0.2 }}
                            className="md:w-1/2 relative"
                        >
                            <img
                                src="/images/hero/Infraestructura.webp"
                                alt="Infraestructura Industrial Laz Metals"
                                className="w-full h-[500px] object-cover rounded-block shadow-lg"
                            />
                            <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-primary/10 rounded-full blur-3xl z-[-1]" />
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* VALUE PROPOSITION GRID (SERVICES SUMMARY) */}
            <section className="py-24 bg-bg-primary">
                <div className="container mx-auto px-6 max-w-6xl">
                    <div className="text-center mb-16">
                        <div className="section-tag-green mb-6 mx-auto inline-block">{t('servicesSum.tag')}</div>
                        <h2 className="text-2xl md:text-5xl font-heading font-medium text-accent">
                            {t('servicesSum.title')}
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5 }}
                            className="bg-white rounded-block shadow-subtle border border-gray-100 hover:-translate-y-2 transition-transform duration-300 flex flex-col items-start overflow-hidden group"
                        >
                            <div className="w-full h-48 bg-gray-200 overflow-hidden relative">
                                <img src="/images/hero/Flota.jpg" alt="Flota Logística" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                            </div>
                            <div className="p-8 flex flex-col flex-grow">
                                <h3 className="text-2xl font-heading font-medium text-accent mb-4">{t('servicesSum.fleetTitle')}</h3>
                                <p className="text-gray-600 font-body leading-relaxed mb-6 flex-grow">
                                    {t('servicesSum.fleetDesc')}
                                </p>
                                <a href="/servicios" className="inline-flex items-center text-primary font-bold text-sm uppercase tracking-wide hover:gap-3 transition-all">
                                    {t('servicesSum.fleetCta')} <ArrowRight size={16} className="ml-2" />
                                </a>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                            className="bg-bg-tertiary rounded-block shadow-lg text-white hover:-translate-y-2 transition-transform duration-300 flex flex-col items-start overflow-hidden group"
                        >
                            <div className="w-full h-48 bg-bg-tertiary overflow-hidden relative">
                                <img src="/images/hero/Compra%20y%20venta.png" alt="Compra sin intermediarios" className="w-full h-full object-cover opacity-60 group-hover:scale-105 transition-transform duration-500 blend-luminosity" />
                                <div className="absolute inset-0 bg-gradient-to-t from-bg-tertiary to-transparent" />
                            </div>
                            <div className="p-8 flex flex-col flex-grow w-full relative z-10">
                                <h3 className="text-2xl font-heading font-medium text-white mb-4">{t('servicesSum.buyTitle')}</h3>
                                <p className="text-gray-300 font-body leading-relaxed mb-6 flex-grow">
                                    {t('servicesSum.buyDesc')}
                                </p>
                                <a href="https://api.whatsapp.com/send?phone=19292902022&text=Hola,%20vengo%20de%20la%20secci%C3%B3n%20de%20servicios.%20Me%20interesa%20estructurar%20una%20oferta%20de%20compra/venta%20directa%20sin%20intermediarios." target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-primary-light font-bold text-sm uppercase tracking-wide hover:gap-3 transition-all">
                                    {t('servicesSum.buyCta')} <ArrowRight size={16} className="ml-2" />
                                </a>
                            </div>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                            className="bg-white rounded-block shadow-subtle border border-gray-100 hover:-translate-y-2 transition-transform duration-300 flex flex-col items-start overflow-hidden group"
                        >
                            <div className="w-full h-48 bg-gray-200 overflow-hidden relative">
                                <img src="/images/hero/Metal%20fundido.png" alt="Suministro a Fundiciones" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                            </div>
                            <div className="p-8 flex flex-col flex-grow">
                                <h3 className="text-2xl font-heading font-medium text-accent mb-4">{t('servicesSum.supplyTitle')}</h3>
                                <p className="text-gray-600 font-body leading-relaxed mb-6 flex-grow">
                                    {t('servicesSum.supplyDesc')}
                                </p>
                                <a href="/catalogo" className="inline-flex items-center text-primary font-bold text-sm uppercase tracking-wide hover:gap-3 transition-all">
                                    {t('servicesSum.supplyCta')} <ArrowRight size={16} className="ml-2" />
                                </a>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </section>

            {/* B2B CONTACT FORM SECTION */}
            <section className="py-24 relative border-b border-gray-100 overflow-hidden bg-bg-secondary/30">
                <div className="container mx-auto px-6 max-w-6xl relative z-10">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-16 lg:gap-24">

                        {/* Left Column - Info */}
                        <div className="lg:col-span-2">
                            <div className="section-tag-orange mb-6 text-orange-500 border-orange-200 bg-white inline-block">{t('contactSummary.tag')}</div>
                            <h2 className="text-2xl md:text-4xl font-heading font-medium text-accent leading-tight mb-8">
                                {t('contactSummary.title')}
                            </h2>
                            <p className="text-gray-600 font-body text-lg leading-relaxed mb-12">
                                {t('contactSummary.desc')}
                            </p>

                            <ul className="space-y-6 font-body">
                                <li className="flex gap-4">
                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-primary shrink-0 shadow-sm border border-gray-100">
                                        <Phone size={18} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 text-sm">Teléfono Corporativo</h4>
                                        <p className="text-gray-500 text-sm">+1 929 290 2022</p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-primary shrink-0 shadow-sm border border-gray-100">
                                        <Mail size={18} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 text-sm">Email Ejecutivo</h4>
                                        <p className="text-gray-500 text-sm">Luislazo@lazmetals.com</p>
                                    </div>
                                </li>
                                <li className="flex gap-4">
                                    <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-primary shrink-0 shadow-sm border border-gray-100">
                                        <MapPin size={18} />
                                    </div>
                                    <div>
                                        <h4 className="font-bold text-gray-800 text-sm">Sede Central</h4>
                                        <p className="text-gray-500 text-sm">Doral, FL 33166</p>
                                    </div>
                                </li>
                            </ul>
                        </div>

                        {/* Right Column - Form */}
                        <div className="lg:col-span-3">
                            <div className="bg-white p-8 md:p-10 rounded-block shadow-2xl border border-gray-100 relative">
                                <ContactForm />
                            </div>
                        </div>

                    </div>
                </div>
            </section>

            {/* TRANSITION CTA BLOCK */}
            <section className="py-24 bg-bg-primary border-t border-gray-100">
                <div className="container mx-auto px-6 max-w-5xl">
                    <div className="rounded-[40px] p-12 md:p-20 text-center relative overflow-hidden group shadow-2xl">
                        <div className="absolute inset-0 bg-cover bg-center transition-transform duration-[15s] group-hover:scale-105" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1616422285623-138271cc2c84?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80)' }} />
                        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

                        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/30 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
                        <div className="absolute bottom-0 left-0 w-64 h-64 bg-orange-500/20 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2" />

                        <div className="relative z-10 text-white">
                            <h2 className="text-2xl md:text-5xl font-heading font-medium mb-6 leading-tight text-white drop-shadow-md">
                                {t('transition.title')}
                            </h2>
                            <p className="text-gray-300 font-body text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
                                {t('transition.desc')}
                            </p>
                            <a href="https://api.whatsapp.com/send?phone=19292902022&text=Hola,%20estoy%20interesado%20en%20iniciar%20una%20negociaci%C3%B3n%20log%C3%ADstica/operativa%20con%20Laz%20Metals." target="_blank" rel="noopener noreferrer" className="btn-primary h-14 px-10 text-base shadow-lg shadow-primary/20">
                                {t('transition.cta')}
                            </a>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Home;
